import { Button, FloatingLabel, Form, Modal, Alert } from "react-bootstrap"
import { useState } from "react"
import { googleAddress } from "../../../Helpers/Property/propertyAddressFormatters"
import './findCoordinatesModal.scss'
import { AddressType } from "../../../Types/addressType"

export function FindCoordinatesModal(props: {
    active: boolean,
    onClose: () => void,
    onCoordinatesFound: (coordinates: number[]) => void,
    address?: AddressType,
    message?: string
    title?: string
}) {

    const {
        active,
        onClose,
        onCoordinatesFound,
        address,
        message = "Follow the simple instructions below to get coordinates from Google Maps",
        title = "Find Coordinates"
    } = props

    const [urlInput, setUrlInput] = useState('')
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')

    // Check if address has required fields (everything except address2)
    const hasRequiredFields = address && 
        address.address1 && 
        address.city && 
        address.state && 
        address.zip && 
        address.country

    const parseCoordinatesFromUrl = (urlStr: string): number[] | null => {
        try {
            // Look for coordinates in various Google Maps URL formats
            // Format 1: @lat,lng,zoom (most common when clicking pin)
            const coordRegex1 = /@(-?\d+\.?\d*),(-?\d+\.?\d*)/
            const match1 = urlStr.match(coordRegex1)
            
            if (match1 && match1[1] && match1[2]) {
                const lat = parseFloat(match1[1])
                const lng = parseFloat(match1[2])
                
                if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
                    return [lng, lat] // Return as [longitude, latitude]
                }
            }

            // Format 2: /maps/place/.../@lat,lng
            const coordRegex2 = /\/maps\/place\/[^@]*@(-?\d+\.?\d*),(-?\d+\.?\d*)/
            const match2 = urlStr.match(coordRegex2)
            
            if (match2 && match2[1] && match2[2]) {
                const lat = parseFloat(match2[1])
                const lng = parseFloat(match2[2])
                
                if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
                    return [lng, lat]
                }
            }

            // Format 3: /maps/@lat,lng
            const coordRegex3 = /\/maps\/@(-?\d+\.?\d*),(-?\d+\.?\d*)/
            const match3 = urlStr.match(coordRegex3)
            
            if (match3 && match3[1] && match3[2]) {
                const lat = parseFloat(match3[1])
                const lng = parseFloat(match3[2])
                
                if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
                    return [lng, lat]
                }
            }

            return null
        } catch (err) {
            return null
        }
    }

    const handleParseCoordinates = () => {
        setError('')
        setSuccess('')

        if (!urlInput.trim()) {
            setError('Please paste the Google Maps URL')
            return
        }

        const coordinates = parseCoordinatesFromUrl(urlInput)
        
        if (coordinates) {
            setSuccess(`Coordinates found: Latitude ${coordinates[1]}, Longitude ${coordinates[0]}`)
            onCoordinatesFound(coordinates)
        } else {
            setError('Could not find valid coordinates in the URL. Please make sure you copied the full URL from Google Maps after clicking on the pin.')
        }
    }

    const handleClose = () => {
        setUrlInput('')
        setError('')
        setSuccess('')
        onClose()
    }

    return (
        <Modal
            show={active}
            onHide={handleClose}
            size="lg"
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    {title}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="mb-4">{message}</p>

                {hasRequiredFields && address ? (
                    <div className="mb-4">
                        <h5>📍 Simple Instructions:</h5>
                        <ol>
                            <li>
                                Click this link to open Google Maps: {' '}
                                <a 
                                    href={googleAddress(address)} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="btn btn-primary btn-sm"
                                >
                                    Open in Google Maps
                                </a>
                            </li>
                            <li>Click on the <strong>red pin/marker</strong> on the map</li>
                            <li>Copy the URL from your browser's address bar</li>
                            <li>Paste it in the text area below</li>
                        </ol>
                    </div>
                ) : (
                    <div className="mb-4">
                        <h5>📍 Simple Instructions:</h5>
                        <ol>
                            <li>Go to <a href="https://maps.google.com" target="_blank" rel="noopener noreferrer">Google Maps</a></li>
                            <li>Search for your address</li>
                            <li>Click on the <strong>red pin/marker</strong> on the map</li>
                            <li>Copy the URL from your browser's address bar</li>
                            <li>Paste it in the text area below</li>
                        </ol>
                        <Alert variant="info">
                            <small><strong>Tip:</strong> Fill in the address fields first to get a direct Google Maps link above.</small>
                        </Alert>
                    </div>
                )}

                <Form.Group className="mb-3">
                    <FloatingLabel label="Paste Google Maps URL here">
                        <Form.Control
                            as="textarea"
                            placeholder="Paste the Google Maps URL here..."
                            style={{ height: '100px' }}
                            value={urlInput}
                            onChange={(e) => setUrlInput(e.target.value)}
                        />
                    </FloatingLabel>
                </Form.Group>

                {error && (
                    <Alert variant="danger">
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert variant="success">
                        {success}
                    </Alert>
                )}

                <div className="d-flex gap-2">
                    <Button
                        variant="primary"
                        onClick={handleParseCoordinates}
                        disabled={!urlInput.trim()}
                    >
                        Extract Coordinates
                    </Button>
                    
                    <Button
                        variant="outline-secondary"
                        onClick={() => {
                            setUrlInput('')
                            setError('')
                            setSuccess('')
                        }}
                    >
                        Clear
                    </Button>
                </div>

                <hr />
                
                <details className="mt-3">
                    <summary className="text-muted" style={{ cursor: 'pointer' }}>
                        <small>Alternative: Manual coordinate entry</small>
                    </summary>
                    <div className="mt-2">
                        <small className="text-muted">
                            If you already know the coordinates, you can enter them directly in the coordinate fields in the main form.
                        </small>
                    </div>
                </details>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    )
}